// EN / DE for the game client (APP_STARTUP §7 "Language"). One table per
// language, filled by the per-module dictionaries in ./i18n/<module>.js
// (register); keys are namespaced ('land.*', 'main.*', 'player.*'). The
// language is picked once at import — ?lang=en|de, then the stored choice
// (localStorage 'geovr.lang'), then navigator.languages, then 'en' — and
// changed only by the landing's EN | DE toggle (setLang). World data (OSM /
// place names, facts from the bake) is never translated.
const LANGS = ['en', 'de'];
const KEY = 'geovr.lang';
const TABLES = { en: Object.create(null), de: Object.create(null) };
const warned = new Set();
const listeners = new Set();

function pick(l) {
  const s = String(l || '').toLowerCase();
  return LANGS.find((x) => s === x || s.startsWith(x + '-')) || null;
}

function resolve() {
  try {
    const q = pick(new URLSearchParams(location.search).get('lang'));
    if (q) return q;
  } catch { /* no location (node) */ }
  try {
    const s = pick(localStorage.getItem(KEY));
    if (s) return s;
  } catch { /* storage off */ }
  try {
    for (const l of navigator.languages || [navigator.language]) {
      const p = pick(l);
      if (p) return p;
    }
  } catch { /* no navigator */ }
  return 'en';
}

let cur = resolve();
if (typeof document !== 'undefined') document.documentElement.lang = cur;

// merge a module's { en: {...}, de: {...} }; a key registered twice throws
export function register(dict) {
  for (const l of LANGS) {
    for (const [k, v] of Object.entries((dict && dict[l]) || {})) {
      if (k in TABLES[l]) throw new Error(`i18n: key '${k}' (${l}) registered twice`);
      TABLES[l][k] = v;
    }
  }
}

// the string for the current language ({name} filled from vars); a key
// missing here falls back to en, one missing in en is the key (warned once)
export function t(key, vars) {
  let s = TABLES[cur][key];
  if (s === undefined) s = TABLES.en[key];
  if (s === undefined) {
    if (!warned.has(key)) { warned.add(key); console.warn(`i18n: no string for '${key}'`); }
    return key;
  }
  return vars ? s.replace(/\{(\w+)\}/g, (m, n) => (n in vars ? String(vars[n]) : m)) : s;
}

export function lang() { return cur; }

export function setLang(l) {
  const p = pick(l);
  if (!p) return;
  cur = p;
  try { localStorage.setItem(KEY, p); } catch { /* storage off */ }
  document.documentElement.lang = p;
  applyI18n(document);
  for (const fn of listeners) {
    try { fn(p); } catch (e) { console.error(e); }
  }
}

export function onLang(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// data-i18n="key" → textContent; data-i18n-attr="placeholder:key;title:key2"
// → attributes; data-i18n-html="key" → innerHTML (OUR dictionary strings
// only, the ones that carry <b> / <a> — never world or player text)
export function applyI18n(root) {
  if (!root || !root.querySelectorAll) return;
  for (const el of root.querySelectorAll('[data-i18n]')) el.textContent = t(el.dataset.i18n);
  for (const el of root.querySelectorAll('[data-i18n-html]')) el.innerHTML = t(el.dataset.i18nHtml);
  for (const el of root.querySelectorAll('[data-i18n-attr]')) {
    for (const pair of el.dataset.i18nAttr.split(';')) {
      const i = pair.indexOf(':');
      if (i > 0) el.setAttribute(pair.slice(0, i).trim(), t(pair.slice(i + 1).trim()));
    }
  }
}
